import React, { FunctionComponent, useState } from "react";
import styles from "./DynamicForm.module.scss";
import DynamicFormProps from "./DynamicForm.interface";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import Select from "react-select";
import Button from "components/Button/Button";
import BlockButton from "blocks/models/block-button.interface";
import ContactForm from "./components/ContactForm/ContactForm";

const DynamicForm: FunctionComponent<DynamicFormProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const [selectedCity, setSelectedCity] = useState<any | null>(null);
    const [selectedActivity, setSelectedActivity] = useState<any | null>(null);
    const [contactMe, setContactMe] = useState<boolean>(false);

    const handleCityChange = (selectedOption: any) => {
        const activity = props.content?.citiesList?.find((item: any) => item.content.cityName === selectedOption?.value);

        setSelectedCity(activity);
        setSelectedActivity(null);
        setContactMe(false);
    };

    const handleActivityChange = (selectedOption: any) => {
        const activity = selectedCity?.content?.activities?.find((a: any) => a.content.activityName === selectedOption?.value);
        setSelectedActivity(activity);
    };

    const activityOptions =
        selectedCity?.content?.activities?.map((activity: any) => ({
            value: activity.content.activityName,
            label: activity.content.activityName,
        })) ?? [];

    const handleToggleForm = () => {
        setContactMe(!contactMe);
    };

    return (
        <section
            className={combineClasses(styles.wrapper, inViewClasses, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles }}
            ref={inViewElementRef}
        >
            <div className={combineClasses(styles.container, ...splitClasses)}>
                <div className={styles.dropdown_container}>
                    <Select
                        className={styles.select}
                        classNamePrefix="select"
                        options={
                            props.content?.citiesList?.map((item: any) => ({
                                value: item.content.cityName,
                                label: item.content.cityName,
                            })) ?? []
                        }
                        onChange={handleCityChange}
                        aria-label=""
                        isSearchable={false}
                        placeholder="Vælg en by"
                        isClearable={false}
                    />

                    {selectedCity && activityOptions.length > 0 && (
                        <div>
                            <Select
                                className={styles.select}
                                classNamePrefix="select"
                                options={activityOptions}
                                placeholder="Vælg et arrangement"
                                value={
                                    selectedActivity
                                        ? {
                                              value: selectedActivity.content.activityName,
                                              label: selectedActivity.content.activityName,
                                          }
                                        : null
                                }
                                onChange={handleActivityChange}
                                isSearchable={false}
                                isClearable={false}
                            />
                        </div>
                    )}
                </div>

                {selectedActivity && (
                    <div className={styles.activity_details}>
                        <div className={styles.buttons}>
                            {/* Read more btn */}
                            {selectedActivity.content.readMoreCTA && selectedActivity.content.readMoreCTA.length > 0 ? (
                                <div className={styles.button}>
                                    {selectedActivity.content.readMoreCTA.map((button: BlockButton) => (
                                        <Button
                                            key={button.contentUdi}
                                            to={button.content.link}
                                            className={styles.button}
                                            variant={button.content.variant}
                                            {...button.content}
                                        >
                                            {button.content.label}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}

                            {/* Toggle form btn */}
                            {selectedActivity.content.toggleFormButton && selectedActivity.content.toggleFormButton.length > 0 ? (
                                <div className={styles.button}>
                                    {selectedActivity.content.toggleFormButton.map((button: BlockButton) => (
                                        <Button
                                            key={button.contentUdi}
                                            className={styles.button}
                                            variant={button.content.variant}
                                            onClick={handleToggleForm}
                                        >
                                            {button.content.label}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}

                {/* Contact form */}
                {selectedActivity && contactMe ? (
                    <ContactForm
                        {...selectedActivity}
                        selectedCity={selectedCity?.content.cityName}
                    />
                ) : null}
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia
                    key={props.contentUdi}
                    {...props.settings.backgroundMedia[0]}
                />
            ) : null}
        </section>
    );
};

export default DynamicForm;
